import type { UploadTicket } from "@closer/shared";

import { api } from "./apiClient";

/**
 * TARAYICIDAN GÜVENLİ YÜKLEME (CLOS-121 `[103]`).
 *
 * ### ⚠ Bayt API'den GEÇMİYOR
 *
 * ADR-0010 D2: dosya doğrudan depolamaya, kısa ömürlü imzalı bir adrese gidiyor. API
 * yalnız izin veriyor ve metadata yazıyor. Baytları API'den geçirseydik istek süresi ve
 * bellek dosya boyutuna bağlanırdı.
 *
 * ### ⚠ İmzalı PUT `api()`'den GEÇMİYOR — tek istisna
 *
 * `api()` her isteğe kimlik çerezini ve `x-tenant-id` başlığını ekliyor; imzalı adres
 * başka bir kökene (depolama) gidiyor ve oraya oturum çerezi göndermek, kimliği alakasız
 * bir servise sızdırmak olurdu. İmza zaten yetkinin kendisi. Bu yüzden burada ham `fetch`
 * var ve `credentials: "omit"` açıkça yazılı — kuralın istisnası, tek satırda ve
 * gerekçesiyle birlikte duruyor.
 *
 * ### ⚠ Üç adım AYRILMIYOR
 *
 * İzin (`upload-url`) → yükleme → tamamlama (`complete`). Tamamlama çağrılmazsa nesne
 * `pending` kalır ve hiçbir yere bağlanamaz: yarım kalan bir yükleme, kullanılabilir bir
 * dosya üretemiyor.
 */

export type YuklenenDosya = { storageObjectId: string; fileName: string };

type TaramaDurumu = { status: string; scanned: boolean };

/** Varsayılan bekleme: iki saniyede bir, iki dakika boyunca. */
const TARAMA_ARALIGI_MS = 2000;
const TARAMA_DENEMESI = 60;

const bekle = (ms: number) => new Promise((coz) => setTimeout(coz, ms));

/**
 * ### ⚠ Dördüncü adım: TARAMAYI BEKLEME
 *
 * `complete` nesneyi KARANTİNAYA alıyor; taramayı kuyruk asenkron koşuyor (ADR-0010 D6).
 * Yükleme biter bitmez dosyayı kullanmaya kalkan çağıran "güvenlik taramasından geçmedi"
 * hatası alıyordu — kullanıcı hiçbir şeyi yanlış yapmadığı hâlde dosyadan sözleşme
 * oluşturmak hiç çalışmıyordu. Bekleme burada, çünkü üç çağıranın üçü de aynı yarışa
 * maruzdu; her birine ayrı ayrı koysaydık biri unutulurdu.
 *
 * ⚠ Karar `status` metnine değil sunucunun `scanned` bayrağına bakıyor: durum makinesi
 * büyürse buradaki koşul sessizce yanlışa dönmesin.
 *
 * ⚠ Bekleme sonsuz değil: tarayıcı arızalanırsa nesne karantinada kalır. Sınırda
 * kullanıcı ne olduğunu öğreniyor ve tekrar deneyebiliyor.
 */
async function taramayiBekle(input: {
  objectId: string;
  tenantId: string;
  araligiMs: number;
  denemeSayisi: number;
}): Promise<void> {
  for (let deneme = 0; deneme < input.denemeSayisi; deneme++) {
    const { data } = await api<TaramaDurumu>(`/api/files/${input.objectId}/status`, {
      tenantId: input.tenantId,
    });
    if (data.scanned) return;
    // Bu iki durum ARTIK DEĞİŞMEZ; beklemek kullanıcıyı boşuna oyalamak olurdu.
    if (data.status === "infected") {
      throw new Error("Dosyada zararlı içerik bulundu; kullanılamaz.");
    }
    if (data.status === "scan_failed") {
      throw new Error("Dosya taranamadı; lütfen yeniden yükleyin.");
    }
    await bekle(input.araligiMs);
  }
  throw new Error("Dosya hâlâ taranıyor; birazdan tekrar deneyin.");
}

export async function uploadFile(input: {
  file: File;
  domain: string;
  entityId: string;
  tenantId: string;
  /** Testler ölçümü hızlandırmak için kısaltıyor; üretimde varsayılan geçerli. */
  taramaAraligiMs?: number;
  taramaDenemesi?: number;
}): Promise<YuklenenDosya> {
  const bilet = await api<UploadTicket>("/api/files/upload-url", {
    method: "POST",
    tenantId: input.tenantId,
    body: JSON.stringify({
      domain: input.domain,
      entityId: input.entityId,
      fileName: input.file.name,
      contentType: input.file.type,
      sizeBytes: input.file.size,
    }),
  });

  const yanit = await fetch(bilet.data.uploadUrl, {
    method: "PUT",
    credentials: "omit",
    headers: { "Content-Type": input.file.type },
    body: input.file,
  });
  if (!yanit.ok) throw new Error("Dosya yüklenemedi");

  await api(`/api/files/${bilet.data.objectId}/complete`, {
    method: "POST",
    tenantId: input.tenantId,
    body: JSON.stringify({}),
  });

  await taramayiBekle({
    objectId: bilet.data.objectId,
    tenantId: input.tenantId,
    araligiMs: input.taramaAraligiMs ?? TARAMA_ARALIGI_MS,
    denemeSayisi: input.taramaDenemesi ?? TARAMA_DENEMESI,
  });

  return { storageObjectId: bilet.data.objectId, fileName: input.file.name };
}
